import { useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import styles from './tableSort.module.css';

type Value = number | string | null | undefined;
type Sort = { key: string; dir: 'asc' | 'desc' };

/**
 * A report table sorted by any of its columns (16 Oct). `columns` reads each
 * sortable column's value from a row. A first press sorts numbers highest
 * first and words A to Z, a second turns it round; an empty value sorts last
 * either way. Unsorted, the rows keep the server's order. `head` draws a
 * column's header as a button that says how it sorts (`aria-sort`).
 */
export function useTableSort<T>(rows: readonly T[], columns: Record<string, (row: T) => Value>) {
  const [sort, setSort] = useState<Sort | null>(null);
  const sorted = useMemo(() => {
    const read = sort ? columns[sort.key] : undefined;
    if (!sort || !read) return rows;
    const sign = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const x = read(a);
      const y = read(b);
      if (x == null || y == null) return x == null && y == null ? 0 : x == null ? 1 : -1;
      return (typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y))) * sign;
    });
    // `columns` is rebuilt each render; the sort it reads follows `sort`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, sort]);

  const head = (key: string, label: ReactNode, { left = false, numeric = true } = {}) => {
    const on = sort?.key === key ? sort.dir : null;
    const Icon = on === 'asc' ? ArrowUp : on === 'desc' ? ArrowDown : ArrowUpDown;
    return (
      <th key={key} scope="col" className={left ? styles.left : undefined} aria-sort={on === 'asc' ? 'ascending' : on === 'desc' ? 'descending' : 'none'}>
        <button type="button" className={styles.sort} data-on={on ?? undefined}
          onClick={() => setSort((current) => (current?.key === key
            ? { key, dir: current.dir === 'desc' ? 'asc' : 'desc' }
            : { key, dir: numeric ? 'desc' : 'asc' }))}>
          {label}<Icon size={12} aria-hidden="true" className={styles.icon} />
        </button>
      </th>
    );
  };
  return { rows: sorted, head };
}

/** A number in its cell over a faint bar of its share of the column's largest. */
export function BarCell({ value, max, children, tone = 'var(--chart-blue)' }: { value: number; max: number; children?: ReactNode; tone?: string }) {
  const share = max > 0 ? Math.max(0, Math.min(1, value / max)) : 0;
  return (
    <td className={styles.barCell} style={{ '--share': `${Math.round(share * 100)}%`, '--bar': tone } as CSSProperties}>
      {children ?? value}
    </td>
  );
}
